import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";

// ===============================================================================
// ATTACHMENTS
// ===============================================================================
export const getAttachments = async (search: string | null = null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "Search", value: search });

	const resp = await apiService.callV2("[MMS].[GetAttachments]", parameters);
	return resp;
};

export const getAssetAttachments = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2(
		"[MMS].[GetAssetAttachments]",
		parameters,
	);
	return resp;
};

export const addLinkAssetAttachments = async (
	AssetId: number | string | null,
	AttachmentId: number | string | number[], // Can be single or array
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const attachmentValue = Array.isArray(AttachmentId)
		? AttachmentId.join(",")
		: AttachmentId;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileIdsCSV", value: attachmentValue });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[LinkAssetAttachments]",
		parameters,
	);
	return resp;
};

export const removeAttachmentLink = async (
	AssetId: string | number | undefined,
	AttachmentId: number,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileId", value: AttachmentId });

	const resp = await apiService.callV2(
		"[MMS].[UnlinkAssetAttachments]",
		parameters,
	);
	return resp;
};

export const updateAssetAttachmentsComments = async (
	AssetId: string | number | undefined,
	AttachmentId: number,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileId", value: AttachmentId });
	parameters.push({ name: "Comments", value: Comments });
	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetAttachmentsComments]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// TEXT (MARKDOWN)
// ===============================================================================

// React Query queryFn: throws on error, returns the markdown string.
export const getMarkDownText = async (
	AssetId: string | number | undefined,
): Promise<string> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: Number.parseInt(AssetId as string) },
	];

	const resp = await apiService.callV2("[MMS].[GetMarkDownText]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error fetching text");

	return get(resp, "data.tables[0].rows[0].MarkDownText", "") as string;
};

export const upsertMarkDownText = async (
	AssetId: string | number | undefined,
	MarkDownText: string | null,
): Promise<void> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: Number.parseInt(AssetId as string) },
		{ name: "MarkDownText", value: MarkDownText },
	];

	const resp = await apiService.callV2(
		"[MMS].[UpsertMarkDownText]",
		parameters,
	);
	if (!resp.ok) throw new Error(resp.message || "Error saving text");
};

// ===============================================================================
// STATISTICS
// ===============================================================================

export const getStatistics = async (AssetId: string | number | undefined) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetStatistics]", parameters);
	return resp;
};

// ===============================================================================
// WORK ORDERS
// ===============================================================================

export const getWorkOrdersToAssetDetails = async (
	AssetId: string | number | null,
	lastNWorkOrders: number,
	SearchText: string | null,
	TypeCode: string | null,
	WorkOrderStatusCode: string | null,
	CraftId: number | string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "LastNWorkOrders", value: lastNWorkOrders });
	parameters.push({ name: "SearchText", value: SearchText });

	if (TypeCode !== "A") parameters.push({ name: "TypeCode", value: TypeCode });
	else parameters.push({ name: "TypeCode", value: null });

	if (WorkOrderStatusCode !== "A")
		parameters.push({
			name: "WorkOrderStatusCode",
			value: WorkOrderStatusCode,
		});
	else parameters.push({ name: "WorkOrderStatusCode", value: null });

	if (AssetId !== "A")
		parameters.push({
			name: "AssetId",
			value: Number.parseInt(AssetId as string),
		});
	else parameters.push({ name: "AssetId", value: null });

	if (CraftId !== "A") parameters.push({ name: "CraftId", value: CraftId });
	else parameters.push({ name: "CraftId", value: null });

	const resp = await apiService.callV2("[MMS].[GetWorkOrders]", parameters);
	return resp;
};
